import { type JSX, useEffect, useState } from 'react';

import { ArrowRightIcon, CheckCircleOutlineIcon, InstagramStickerIcon } from '@change/ds-icons';

import { Button, Image, Text } from '@change/ds-core-ui/next';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import stickerLinkImage from 'src/assets/share/sticker_link.png';

type Props = {
	onContinue: () => Promise<void>;
	shareVisualPreview: File | string | null;
};

export function EducationModal({ onContinue, shareVisualPreview }: Props): JSX.Element {
	const { translate } = useI18n();
	const [imageUrl, setImageUrl] = useState<string | null>(null);

	// eslint-disable-next-line consistent-return
	useEffect(() => {
		if (shareVisualPreview instanceof File) {
			const url = URL.createObjectURL(shareVisualPreview);
			// FIXME this could be done with a useMemo, but it could cause issues on SSR due to the URL API
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setImageUrl(url);

			return () => {
				URL.revokeObjectURL(url);
			};
		}
		setImageUrl(shareVisualPreview);
	}, [shareVisualPreview]);

	// Leaving this for now to prevent the user from scrolling the page while the modal is open
	// This will not be needed anymore once the modal is updated to use the new sunrise modal
	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, []);

	return (
		<div className="mt-4 flex flex-col" aria-label={translate('fe.components.psf.instagram.modal_name')}>
			<div className="flex flex-col items-stretch gap-6 py-6">
				<div className="flex flex-col gap-6">
					<div className="flex items-center justify-center gap-3">
						<InstagramStickerIcon className="drop-shadow-[0_4px_4px_rgba(0,0,0,0.25)]" size="x-large" />
						<ArrowRightIcon />
						<div>
							<Image src={stickerLinkImage} alt={translate('corgi.psf.share.modals.body.sticker_link_image_alt')} />
						</div>
					</div>

					<Text as="p" size="x-small" fontWeight="bold" variant="heading" className="px-10 text-center">
						<Translate value="corgi.psf.share.modals.body.description" />
					</Text>
				</div>
				<div className="flex flex-col gap-4">
					{!!imageUrl && (
						<div className="bg-background-raised rounded-lg p-4">
							<Image
								className="m-auto max-h-40 max-w-40 rounded-lg object-contain"
								aspectRatio="1:1"
								src={imageUrl}
								alt={translate('corgi.psf.share.modals.body.preview_image_alt')}
							/>
						</div>
					)}
					<div className="bg-fill-success-weaker border-stroke-success-weak mb-4 flex items-center justify-center gap-3 rounded-lg border p-6">
						<CheckCircleOutlineIcon />
						<Text size="large" className="text-typography-success font-semibold">
							<Translate value="corgi.psf.share.modals.body.link_copied_confirmation" />
						</Text>
					</div>
				</div>
			</div>
			<div className="border-stroke-weaker border-t py-4 pb-0">
				<Button
					variant="primary"
					onClick={async () => onContinue()}
					data-testid="instagram-stories-share-continue"
					data-qa="instagram-stories-share-continue"
					className="w-full"
				>
					<Translate value="corgi.psf.share.modals.body.button" />
				</Button>
			</div>
		</div>
	);
}
